import React, {Component} from "react";
import {withTranslation} from "react-i18next";
import {Category, I18N, Question} from "../../interfaces";
import {KBSettings} from "../../interfaces/settings";
import {UpdateCategoryViews} from "../../services";
import "../../static/css/general.css";
import "../../static/css/faq-widget.css";
import {withHtmlParser, withParams} from "../../utilities/hooks";
import Icon from "../Icon";
import Search from "./Search";
import {AlertError, AlertSuccess, SetAllQuestions} from "../../reducers/store";
import HeaderBackBtn from "./ui/HeaderBackBtn";
import SearchBtn from "./ui/SearchBtn";
import Footer from "./ui/Footer";
import {Button} from "src/components/ui/button";
import {KnowledgeBase} from "src/interfaces/knowledgeBase";
import {connect} from "react-redux";
import {scanKnowledgeBase} from "src/utilities/scanKB";
import {
  CreateEnquiry,
  DownvoteQuestion,
  GetQuestions,
  GetWidgetForm,
  PostMail,
  PostWidgetForm,
  PostWidgetFormSubmission,
  UpdateQuestionViews,
  UpvoteQuestion,
} from "src/services/backend";
import {Mail} from "src/interfaces/mail";
import Spinner from "./ui/Spinner";
import {Form as IForm, FormContent, FormState, FormGroup} from "src/interfaces/form";
import Form from "./Form";
import Joi from "joi";
import {getCountryFlag, modifyHtmlLinks} from "src/utilities/utils";
import {PhoneContact} from "src/interfaces/contacts";
import Header from "./Header";
import {FormGroupSelectionMenu} from "../forms/FormGroupSelectionMenu";
import {FormSelectionMenu} from "../forms/FormSelectionMenu";
import AssistantChat from "../assistant/AssistantChat";
import {processFormForAssistant} from "src/utilities/processDataForAssistants";
import {FORM_FIELDS_TO_EXCLUDE} from "src/config/constants";
import {Assistant, AssistantChannel} from "src/interfaces/assistant";

export enum WidgetContent {
  MAIN = "MAIN",
  CATEGORY = "CATEGORY",
  CHAT = "CHAT",
  MAIL = "MAIL",
  QA_SUGGESTIONS = "QA_SUGGESTIONS",
  QUESTION = "QUESTION",
  FORM_GROUP_SELECT = "FORM_GROUP_SELECT",
  FORM_SELECT = "FORM_SELECT",
  FORM = "FORM",
  PHONE_CONTACTS = "PHONE_CONTACTS",
  ASSISTANT_CHAT = "ASSISTANT_CHAT",
}

interface WidgetProps extends I18N {
  language: string;
  getInnerText: Function;
  parser: Function;
  t: Function;
  inProgress: boolean;
  knowledgeBases: KnowledgeBase[];
  currentKnowledgeBase: KnowledgeBase;
  categories: Category[];
  questions: Question[];
  settings: KBSettings;
  formsOnly?: boolean;
  formOnly?: boolean;
  params: {
    uniqueId: string;
    formId?: string;
    lang?: string;
  };
}

interface WidgetState {
  language: string;
  showFAQPopup: boolean;
  showMailPopup: boolean;
  searchText: string;
  content: WidgetContent;
  selectedCategoryId: number;
  selectedQuestion: Question;
  suggestedQuestions: Question[];

  mailAddress: string;
  mailSubject: string;
  mailBody: string;
  fieldErrors: any;

  selectedForm: IForm | null;
  selectedFormGroup: FormGroup | null;
  formContent: FormContent | null;
  formState: FormState;
  validatedForm: Joi.ValidationResult;

  navigationHistory: {
    content: WidgetContent;
    selectedCategoryId: number;
    selectedQuestion: Question;
  }[];
  isFeedbackGiven: boolean;
  chatAssistant: Assistant | null;
  chatLoading: boolean;
  inProgress: boolean;
  isSubmitting: boolean;
  isMobileView: boolean | null;
}

class Widget extends Component<WidgetProps, WidgetState> {
  constructor(props) {
    super(props);
    this.state = {
      language: "",
      content: WidgetContent.MAIN,
      showFAQPopup: false,
      showMailPopup: false,
      searchText: null,
      selectedCategoryId: null,
      selectedQuestion: null,
      suggestedQuestions: [],

      mailAddress: "",
      mailBody: "",
      mailSubject: `Query on ${this.props.currentKnowledgeBase?.name}`,
      fieldErrors: {},

      selectedForm: null,
      selectedFormGroup: null,
      formContent: null,
      formState: {},
      validatedForm: null,

      navigationHistory: [],
      isFeedbackGiven: false,
      chatAssistant:
        this.props.settings?.assistantSettings?.assistants.find(
          (assistant) => assistant.channel === AssistantChannel.CHAT,
        ) || null,
      chatLoading: true,
      inProgress: this.props.inProgress,
      isSubmitting: false,
      isMobileView: null,
    };
  }

  componentDidMount(): void {
    this.initializeLanguage();
    this.attachLinkHandlers();
    window.addEventListener("message", this.handleParentMessage);
    if (this.props.formsOnly) {
      this.setState({content: WidgetContent.FORM_GROUP_SELECT});
    }

    if (this.props.formOnly) {
      const formId = Number(this.props.params.formId);
      this.setWidgetForm(formId);
    }
  }

  componentDidUpdate(prevProps: Readonly<WidgetProps>): void {
    if (prevProps.inProgress !== this.props.inProgress) {
      const contextKnowlegeBase = this.props.knowledgeBases?.find((kb) => kb.uniqueId === this.props.params.uniqueId);
      this.setState({
        inProgress: this.props.inProgress,
        mailSubject: `Query on ${contextKnowlegeBase?.name}`,
      });
    }
    if (this.props.settings && this.props.settings !== prevProps.settings) {
      this.initializeLanguage();
      if (this.props.settings.widgetSettings.assistantChatOnlyMode)
        this.setState({content: WidgetContent.ASSISTANT_CHAT});
      if (this.props.settings.assistantSettings?.assistants?.length) {
        this.setState({
          chatAssistant: this.props.settings?.assistantSettings?.assistants.find(
            (assistant) => assistant.channel === AssistantChannel.CHAT,
          ),
        });
      }
    }

    this.attachLinkHandlers();
  }

  componentWillUnmount(): void {
    this.removeLinkHandlers();
    window.removeEventListener("message", this.handleParentMessage);
  }

  async setWidgetForm(formId: number) {
    try {
      const formContent = await GetWidgetForm(formId);
      const name = formContent.data.layout.title;

      this.setState({
        selectedForm: {name, id: formId},
        formContent: formContent.data,
        content: WidgetContent.FORM,
      });
    } catch (error) {
      console.error("Error fetching form content:", error);
      AlertError(this.props.t("FORM_NOT_FOUND"));
    }
  }

  render() {
    const fontFamily = this.props.settings?.widgetSettings?.fontFamily || "Roboto";

    const widgetBackgroundColor =
      this.props.formOnly || this.props.formsOnly
        ? this.props.settings?.widgetSettings?.formsOnlyBackgroundColor
        : "white";

    return (
      this.props.settings &&
      !this.state.inProgress && (
        <React.Fragment>
          <div
            className={`faq-widget-container`}
            style={{
              width: "100vw",
              height: "100vh",
              fontFamily: fontFamily,
              backgroundColor: widgetBackgroundColor,
            }}
          >
            {this.getWidgetContent()}
          </div>
        </React.Fragment>
      )
    );
  }

  getWidgetContent(): React.ReactNode {
    switch (this.state.content) {
      case WidgetContent.MAIN:
        return this.getMainContent();
      case WidgetContent.CATEGORY:
        return this.getCategoryContent();
      case WidgetContent.CHAT:
        return this.getChatContent();
      case WidgetContent.MAIL:
        return this.getMailBoxContent();
      case WidgetContent.QA_SUGGESTIONS:
        return this.getSuggestionsContent();
      case WidgetContent.QUESTION:
        return this.getQuestionContent();
      case WidgetContent.FORM_GROUP_SELECT:
        return this.getFormGroupSelectionContent();
      case WidgetContent.FORM_SELECT:
        return this.getFormSelectionContent();
      case WidgetContent.FORM:
        return this.getFormContent();
      case WidgetContent.PHONE_CONTACTS:
        return this.getPhoneContacts();
      case WidgetContent.ASSISTANT_CHAT:
        return this.getAssistantChatContent();
    }
  }

  getMainContent(): React.ReactNode {
    const categoryColumns = this.props.settings?.widgetSettings?.categoryColumns || 3;
    const categoriesInLang = this.props.categories.filter((c) => c.langs[this.state.language]);

    return (
      <React.Fragment>
        <Header
          widgetSettings={this.props.settings?.widgetSettings}
          languageCode={this.state.language}
          showTitle={true}
          showSearch={false}
          showBackButton={false}
          showAssistantChat={this.props.settings?.widgetSettings?.assistantChatEnabled && !!this.state.chatAssistant}
          closeButton={this.getCloseButton()}
          onAssistantChatButtonClick={() => this.setState({content: WidgetContent.ASSISTANT_CHAT})}
          onBackButtonClick={() => this.handleBackButtonClick()}
          onSearchButtonClick={() => this.handleSearchButtonClick()}
        />
        <main className="faq-widget-main">
          <div className="faq-widget-main-top">
            {this.props.settings?.widgetSettings?.langs?.[this.state.language]?.welcomeText && (
              <h2 className="faq-widget-heading-welcome-text">
                {this.props.settings?.widgetSettings?.langs[this.state.language]?.welcomeText}
              </h2>
            )}
            <Search
              language={this.state.language}
              placeholder={this.props.settings?.widgetSettings?.langs?.[this.state.language]?.searchBarPlaceholderText}
              aria-label={this.props.settings?.widgetSettings?.langs?.[this.state.language]?.searchBarPlaceholderText}
              showAnswers={false}
              questionClickHandler={(question: Question) => this.handleQuestionClick(question)}
            />
            <h3>
              {this.props.settings?.widgetSettings?.langs?.[this.state.language]?.categoriesTitle ||
                this.props.t("CATEGORIES")}
            </h3>
            <div
              className={`faq-widget-categories-container ${
                this.props.settings?.widgetSettings?.footerEnabled ? "mb-[88px]" : "mb-[70px]"
              }`}
            >
              {categoriesInLang
                .sort((a, b) => b.langs?.[this.state.language]?.views - a.langs?.[this.state.language]?.views)
                .filter((c) =>
                  this.props.settings?.widgetSettings?.onlyShowRootCategoriesInMain ? c.parentId === 0 : true,
                )
                .slice(0, this.props.settings?.widgetSettings?.numberOfCategories || 6)
                .map((category) => {
                  const categoryName = category.langs?.[this.state.language]?.name;

                  if (!categoryName) {
                    return null;
                  }
                  return (
                    <button
                      style={{
                        backgroundColor: `${this.props.settings?.widgetSettings?.categoryButtonColor}`,
                        color: `${this.props.settings?.widgetSettings?.categoryButtonTextColor}`,
                      }}
                      className={`faq-widget-category ${
                        categoryColumns === 1 ? "w-full" : categoryColumns === 2 ? "w-1/3" : ""
                      } font-semibold hover:shadow truncate`}
                      key={category.id}
                      title={categoryName}
                      onClick={(e) => this.handleCategoryClick(category.id)}
                      aria-label={categoryName}
                    >
                      <span className="truncate">{categoryName}</span>
                    </button>
                  );
                })}
            </div>
          </div>
          <div className="faq-widget-main-footer">
            {this.getContacts()}
            {this.props.settings?.widgetSettings?.footerEnabled && <Footer />}
          </div>
        </main>
      </React.Fragment>
    );
  }

  getContacts(): React.ReactNode {
    const style = {
      style: {
        backgroundColor: this.props.settings?.widgetSettings?.buttonColor,
      },
    };

    return (
      <div className="faq-widget-contacts-container">
        <div className="faq-widget-contact-bar-icons" role="toolbar" aria-label={this.props.t("CONTACT_OPTIONS")}>
          {this.props.settings?.widgetSettings?.chatEnabled && (
            <button
              className="faq-widget-contact-bar-icon"
              onClick={(e) => this.handleChatIconClick(e)}
              aria-label={this.props.t("START_CHAT")}
              title={this.props.t("START_CHAT")}
              {...style}
            >
              <Icon name="message-circle" iconSize="24px" color="white" />
              <span className="sr-only">{this.props.t("START_CHAT")}</span>
            </button>
          )}
          {this.props.settings?.widgetSettings?.formEnabled &&
            this.props.settings?.widgetSettings?.langs?.[this.state.language]?.formGroups?.length > 0 && (
              <button
                className="faq-widget-contact-bar-icon"
                onClick={(e) => this.handleFormIconClick()}
                aria-label={this.props.t("OPEN_CONTACT_FORM")}
                title={this.props.t("OPEN_CONTACT_FORM")}
                {...style}
              >
                <Icon name="mail" iconSize="24px" color="white" />
                <span className="sr-only">{this.props.t("OPEN_CONTACT_FORM")}</span>
              </button>
            )}
          {!this.props.settings?.widgetSettings?.formEnabled && this.props.settings?.websiteSettings?.mailbox && (
            <button
              className="faq-widget-contact-bar-icon"
              onClick={(e) => this.handleMailIconClick()}
              aria-label={this.props.t("CONTACT_US_BY_MAIL")}
              title={this.props.t("CONTACT_US_BY_MAIL")}
              {...style}
            >
              <Icon name="mail" iconSize="24px" color="white" />
              <span className="sr-only">{this.props.t("CONTACT_US_BY_MAIL")}</span>
            </button>
          )}
          {this.props.settings?.widgetSettings?.phoneContactsEnabled && (
            <button
              className="faq-widget-contact-bar-icon"
              onClick={(e) => this.handlePhoneContactsIconClick()}
              aria-label={this.props.t("CONTACT_US_BY_PHONE")}
              title={this.props.t("CONTACT_US_BY_PHONE")}
              {...style}
            >
              <Icon name="phone-call" iconSize="24px" color="white" />
              <span className="sr-only">{this.props.t("CONTACT_US_BY_PHONE")}</span>
            </button>
          )}
        </div>
        <h3>{this.props.settings?.widgetSettings?.langs?.[this.state.language]?.contactBarText}</h3>
      </div>
    );
  }

  getCategoryContent(): React.ReactNode {
    const categoryColumns = this.props.settings?.widgetSettings?.categoryColumns || 3;

    const selectedCategory = this.props.categories.find((c) => c.id === this.state.selectedCategoryId);
    const questionsInSelectedCategory = this.props.questions.filter(
      (q) => q.categoryId === this.state.selectedCategoryId,
    );
    const {child: childCategories, breads} = this.getChildCategories();
    const selectedCategoryName = selectedCategory?.langs?.[this.state.language]?.name;

    const {showContactsInCategoryAndQuestion, footerEnabled} = this.props.settings?.widgetSettings;
    const paddingBottom = showContactsInCategoryAndQuestion ? "108px" : "24px";

    UpdateCategoryViews(
      this.props.currentKnowledgeBase.id,
      selectedCategory.id,
      selectedCategory?.langs?.[this.state.language]?.id,
    );

    return (
      <React.Fragment>
        <Header
          widgetSettings={this.props.settings?.widgetSettings}
          languageCode={this.state.language}
          showTitle={false}
          showSearch={true}
          showBackButton={true}
          closeButton={this.getCloseButton()}
          showAssistantChat={this.props.settings?.widgetSettings?.assistantChatEnabled && !!this.state.chatAssistant}
          onAssistantChatButtonClick={() => this.setState({content: WidgetContent.ASSISTANT_CHAT})}
          onBackButtonClick={() => this.handleBackButtonClick()}
          onSearchButtonClick={() => this.handleSearchButtonClick()}
        />
        <main className="faq-widget-main" style={{paddingBottom}}>
          <div className="faq-widget-main-top">
            {breads && (
              <div className="mb-2">
                {breads?.map((bread) => {
                  if (breads.length === 1) {
                    return null;
                  }
                  return (
                    <span
                      key={bread.categoryId}
                      className="text-sm hover:underline hover:cursor-pointer"
                      onClick={() => this.handleCategoryClick(bread.categoryId)}
                    >
                      {bread.name}
                      {breads.indexOf(bread) < breads.length - 1 ? " > " : ""}
                    </span>
                  );
                })}
              </div>
            )}
            <span className="font-bold text-lg">{selectedCategoryName}</span>
            {childCategories?.length > 0 && (
              <div className="faq-widget-categories-container my-4 ">
                {childCategories.map((c) => {
                  const categoryName = c.langs[this.state.language]?.name;
                  return (
                    <button
                      className={`faq-widget-category font-semibold truncate hover:shadow ${
                        categoryColumns === 1 ? "w-full" : categoryColumns === 2 ? "w-1/3" : ""
                      }`}
                      style={{
                        background: `${this.props.settings?.widgetSettings?.categoryButtonColor}`,
                        color: `${this.props.settings?.widgetSettings?.categoryButtonTextColor}`,
                      }}
                      key={c.id}
                      onClick={() => this.handleCategoryClick(c.id)}
                      title={categoryName}
                    >
                      {categoryName}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="">
              {childCategories?.length > 0 && questionsInSelectedCategory?.length > 0 && (
                <h3 className="text-xl">
                  {this.props.settings?.settings?.langs?.[this.state.language]?.allQuestionsInCategoryText ||
                    this.props.t("ALL_QUESTIONS_IN_CATEGORY")}
                  {` ${selectedCategoryName}`}
                </h3>
              )}
              {questionsInSelectedCategory.map((q) => {
                const question = q.langs?.[this.state.language]?.question;
                if (
                  q.langs?.[this.state.language]?.hidden ||
                  q.langs?.[this.state.language]?.internal ||
                  q.deleted ||
                  !question
                ) {
                  return null;
                }
                return (
                  <button
                    className="faq-widget-question hover:shadow truncate"
                    style={{
                      border: `2px solid ${this.props.settings?.widgetSettings?.categoryButtonColor}`,
                    }}
                    key={`question-${q.id}`}
                    aria-label={question}
                    title={question}
                    onClick={(e) => this.handleQuestionClick(q)}
                  >
                    <span className="faq-widget-question-text truncated-text">{question}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {showContactsInCategoryAndQuestion && (
            <div className="faq-widget-main-footer">
              {showContactsInCategoryAndQuestion && this.getContacts()}
              {footerEnabled && showContactsInCategoryAndQuestion && <Footer />}
            </div>
          )}
          {footerEnabled && !showContactsInCategoryAndQuestion && <Footer />}
        </main>
      </React.Fragment>
    );
  }

  getChildCategories() {
    const getChildCategoriesRecursively = (
      categoryId: number,
      categories: Category[],
    ): {parent: Category; child: Category[]; breads: {categoryId: number; name: string}[]} | null => {
      if (!Array.isArray(categories) || !categories) {
        return null;
      }
      for (const category of categories) {
        const categoryName = category.langs?.[this.state.language]?.name;
        if (category.id === categoryId) {
          return {
            parent: category,
            child: category.childrenCategories,
            breads: [{categoryId: category.id, name: categoryName}],
          };
        }

        const result = getChildCategoriesRecursively(categoryId, category.childrenCategories);
        if (result) {
          result.breads.unshift({categoryId: category.id, name: categoryName});
          return result;
        }
      }
      return null;
    };

    return getChildCategoriesRecursively(this.state.selectedCategoryId, this.props.categories);
  }

  getChatContent(): React.ReactNode {
    window.addEventListener("message", (event) => {
      const loaded = event.data?.type === "CHAT_LOADED";
      if (loaded) {
        setTimeout(() => this.setState({chatLoading: false}), 150);
      }
    });

    return (
      <React.Fragment>
        <div className="faq-widget-header">
          <span className="absolute right-[8rem] top-4 z-50 bg-white h-8 flex items-center">
            <HeaderBackBtn
              hideText={true}
              handleBackButtonClick={() => {
                this.handleBackButtonClick();
              }}
              setLoading={() => this.setState({chatLoading: true})}
            />
          </span>
          <div className="absolute right-3 top-3">{this.getCloseButton()}</div>
        </div>
        {this.state.chatLoading && (
          <div className="h-full w-full absolute top-0 left-0 bg-white flex items-center justify-center text-2xl">
            <Spinner />
          </div>
        )}
        <iframe
          title="Chat"
          style={{width: "100%", height: "100%", border: "1px solid white"}}
          // src={`https://david.connectel.io/connectel-chat/chat.html?id=1`}
          // src={`http://localhost:8081/chat.html?id=1`}
          // src={`${Config.chatDomain}/connectel-chat/chat.html?id=${this.state.settings.widgetSettings.chatId}`}
          src={`${this.props.settings?.widgetSettings?.chatHostName}/connectel-chat/chat.html?id=${this.props.settings?.widgetSettings?.chatId}`}
        />
      </React.Fragment>
    );
  }

  getMailBoxContent(): React.ReactNode {
    const mailBoxTitle = this.props.settings?.widgetSettings?.langs?.[this.state.language]?.mailboxTitle;
    const mailBoxDescription = this.props.settings?.widgetSettings?.langs?.[this.state.language]?.mailboxDescription;
    const buttonColor = this.props.settings?.widgetSettings?.categoryButtonColor;

    return (
      <React.Fragment>
        <header className="faq-widget-header">
          <HeaderBackBtn
            handleBackButtonClick={() => this.handleBackButtonClick()}
            text={this.props.settings?.widgetSettings?.langs?.[this.state.language]?.backButtonText}
          />
          {this.getCloseButton()}
        </header>

        <div className="faq-widget-main">
          <div className="flex flex-col gap-2">
            {mailBoxTitle && <h2 className="text-xl">{mailBoxTitle}</h2>}
            {mailBoxDescription && <p>{mailBoxDescription}</p>}
            <input
              placeholder={
                this.props.settings?.settings?.langs?.[this.state.language]?.emailPlaceholder || this.props.t("EMAIL")
              }
              value={this.state.mailAddress || ""}
              onChange={(e) => {
                this.setState({mailAddress: e.target.value});
                if (this.state.fieldErrors.mailAddress) {
                  this.validateMailInput(e.target.value);
                }
              }}
              onBlur={(e) => {
                this.validateMailInput(e.target.value);
              }}
              style={{
                border: `2px solid ${this.state.fieldErrors.mailAddress ? "red" : buttonColor}`,
              }}
              className={`rounded-lg shadow-md p-2 focus-visible:outline-none ${
                this.state.fieldErrors.mailAddress ? "border-destructive border-2" : ""
              }`}
            />
            <textarea
              placeholder={
                this.props.settings?.settings?.langs?.[this.state.language]?.messagePlaceholder ||
                this.props.t("MESSAGE")
              }
              value={this.state.mailBody || ""}
              onChange={(e) => {
                this.validateMailInput(this.state.mailAddress);
                this.setState({mailBody: e.target.value});
              }}
              style={{border: `2px solid ${buttonColor}`}}
              className={`h-56 rounded-lg shadow-md resize-none p-2 focus-visible:outline-none ${
                this.state.fieldErrors.mailBody ? "border-destructive border-2" : ""
              }`}
            />
          </div>
          <Button
            className="rounded-full mt-2 w-fit"
            style={{
              backgroundColor: this.props.settings?.widgetSettings?.categoryButtonColor,
              color: `${this.props.settings?.widgetSettings?.categoryButtonTextColor}`,
              border: `1px solid ${this.props.settings?.widgetSettings?.categoryButtonColor}`,
            }}
            onClick={(e) => this.handleMailSubmitClick()}
          >
            <span>
              {this.props.settings?.settings?.langs?.[this.state.language]?.sendMessageText || this.props.t("SEND")}
            </span>
          </Button>
          {this.props.settings?.widgetSettings?.footerEnabled && <Footer />}
        </div>
      </React.Fragment>
    );
  }

  getQuestionContent(): React.ReactNode {
    const categoryOfQuestion = this.props.categories?.find((c) => c.id === this.state.selectedQuestion?.categoryId);
    const categoryOfQuestionName = categoryOfQuestion?.langs?.[this.state.language]?.name;
    const parentCategory = this.props.categories?.find((c) => c.id === categoryOfQuestion?.parentId);
    const buttonColor = this.props.settings?.widgetSettings?.categoryButtonColor;
    const buttonTextColor = this.props.settings?.widgetSettings?.categoryButtonTextColor;
    const question = this.state.selectedQuestion?.langs?.[this.state.language]?.question;
    const answer = this.state.selectedQuestion?.langs?.[this.state.language]?.answer;

    const {showContactsInCategoryAndQuestion, footerEnabled} = this.props.settings?.widgetSettings || {};
    const paddingBottom = showContactsInCategoryAndQuestion ? "108px" : "24px";

    let parentCategoryName = "";
    if (parentCategory) {
      parentCategoryName = parentCategory?.langs?.[this.state.language]?.name || "";
    }

    if (!this.state.selectedQuestion || !categoryOfQuestion) {
      return null;
    }

    return (
      <React.Fragment>
        <Header
          widgetSettings={this.props.settings?.widgetSettings}
          languageCode={this.state.language}
          showTitle={false}
          showSearch={true}
          showBackButton={true}
          closeButton={this.getCloseButton()}
          showAssistantChat={this.props.settings?.widgetSettings?.assistantChatEnabled && !!this.state.chatAssistant}
          onAssistantChatButtonClick={() => this.setState({content: WidgetContent.ASSISTANT_CHAT})}
          onBackButtonClick={() => this.handleBackButtonClick()}
          onSearchButtonClick={() => this.handleSearchButtonClick()}
        />
        <main className="faq-widget-main">
          <div className="faq-widget-main-top" style={{paddingBottom}}>
            <div className="flex gap-2">
              {parentCategory && (
                <>
                  <p
                    className="text-xsmall  pb-2 hover:underline hover:cursor-pointer"
                    onClick={() => this.handleCategoryClick(parentCategory?.id)}
                  >
                    {parentCategoryName}
                  </p>
                  <p className="text-xsmall pb-2">{`>`}</p>
                </>
              )}
              <p
                className="text-xsmall pb-2 hover:underline hover:cursor-pointer"
                onClick={() => this.handleCategoryClick(categoryOfQuestion?.id)}
              >
                {categoryOfQuestionName}
              </p>
            </div>
            <div>
              <h2>{question}</h2>
            </div>
            <div className="faq-widget-question-answer unreset">
              <div
                dangerouslySetInnerHTML={{
                  __html: modifyHtmlLinks(answer),
                }}
              />
            </div>
            {this.state.selectedQuestion?.langs?.[this.state.language]?.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {this.state.selectedQuestion?.langs?.[this.state.language].attachments?.map((attachment) => {
                  return (
                    <div className="flex flex-col gap-1" key={attachment.name}>
                      <span className="font-medium">{attachment.description}</span>
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 w-fit"
                        style={{backgroundColor: buttonColor, color: buttonTextColor}}
                        onClick={() => window.open(attachment.s3URL, "_blank", "noopener,noreferrer")}
                      >
                        <Icon name="attachment"></Icon>
                        {attachment.name}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {!this.state.isFeedbackGiven ? (
              <div className="mt-4">
                <span>
                  {this.props.settings?.settings?.langs?.[this.state.language]?.helpfulText ||
                    this.props.t("WAS_THIS_HELPFUL")}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    style={{backgroundColor: buttonColor, color: buttonTextColor}}
                    onClick={() => this.upVoteQuestion()}
                  >
                    {this.props.settings?.settings?.langs?.[this.state.language]?.yesText || this.props.t("YES")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    style={{backgroundColor: buttonColor, color: buttonTextColor}}
                    onClick={() => this.downVoteQuestion()}
                  >
                    {this.props.settings?.settings?.langs?.[this.state.language]?.noText || this.props.t("NO")}
                  </Button>
                </div>
              </div>
            ) : (
              <span className="mt-4">
                {this.props.settings?.settings?.langs?.[this.state.language]?.thanksForFeedback ||
                  this.props.t("THANKS_FOR_FEEDBACK")}
              </span>
            )}
          </div>
          {showContactsInCategoryAndQuestion && (
            <div className="faq-widget-main-footer">
              {showContactsInCategoryAndQuestion && this.getContacts()}
              {footerEnabled && showContactsInCategoryAndQuestion && <Footer />}
            </div>
          )}
          {footerEnabled && !showContactsInCategoryAndQuestion && <Footer />}
        </main>
      </React.Fragment>
    );
  }

  getSuggestionsContent(): React.ReactNode {
    return (
      <React.Fragment>
        <Header
          widgetSettings={this.props.settings?.widgetSettings}
          languageCode={this.state.language}
          showTitle={false}
          showSearch={true}
          showBackButton={true}
          closeButton={this.getCloseButton()}
          showAssistantChat={this.props.settings?.widgetSettings?.assistantChatEnabled && !!this.state.chatAssistant}
          onAssistantChatButtonClick={() => this.setState({content: WidgetContent.ASSISTANT_CHAT})}
          onBackButtonClick={() => this.handleBackButtonClick()}
          onSearchButtonClick={() => this.handleSearchButtonClick()}
        />
        <div className="faq-widget-main">
          <div className="faq-widget-main-top">
            {this.state.suggestedQuestions.length > 0 ? (
              <>
                <p className="text-xl">
                  {this.props.settings?.settings?.langs?.[this.state.language]?.helpfulText ||
                    this.props.t("ARE_THESE_QUESTIONS_HELPFUL_TEXT")}
                </p>
                <div className="flex flex-wrap my-2">
                  {this.state.suggestedQuestions.slice(0, 5).map((question) => {
                    const questionText = question.langs?.[this.state.language]?.question;
                    return (
                      <button
                        className="faq-widget-question"
                        style={{
                          border: `2px solid ${this.props.settings?.widgetSettings?.categoryButtonColor}`,
                        }}
                        key={`question-${question.id}`}
                        title={questionText}
                        onClick={() => this.handleQuestionClick(question)}
                      >
                        <div className="faq-widget-question-text truncated-text">{questionText}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="mb-6">
                {this.props.settings?.settings?.langs?.[this.state.language]?.haveYouAlreadySearchedText ||
                  this.props.t("HAVE_YOU_ALREADY_SEARCHED_TEXT")}
              </p>
            )}
            <div
              className="rounded flex flex-col items-center p-2"
              style={{backgroundColor: `${this.props.settings?.widgetSettings?.categoryButtonColor}`}}
            >
              <p className="text-center">
                {this.props.settings?.settings?.langs?.[this.state.language]?.stillSendMessageText ||
                  this.props.t("WOULD_YOU_STILL_LIKE_TO_SEND_US_YOUR_MESSAGE")}
              </p>
              <Button
                style={{backgroundColor: this.props.settings?.widgetSettings?.buttonColor}}
                onClick={(e) => {
                  if (this.state.validatedForm) {
                    this.submitForm(this.state.selectedForm.id);
                  } else {
                    this.sendMail();
                  }
                }}
                disabled={this.state.isSubmitting}
                className="w-fit min-w-[75px]  rounded-full m-2 relative"
              >
                {this.state.isSubmitting ? (
                  <Spinner size="small" />
                ) : (
                  this.props.settings?.settings?.langs?.[this.state.language]?.sendMessageText || this.props.t("SEND")
                )}
              </Button>
            </div>
          </div>
          {this.props.settings?.widgetSettings?.footerEnabled && <Footer />}
        </div>
      </React.Fragment>
    );
  }

  getFormGroupSelectionContent(): React.ReactNode {
    const formGroups = this.props.settings?.widgetSettings?.langs?.[this.state.language]?.formGroups;

    return (
      <>
        {!this.props.formsOnly && (
          <Header
            widgetSettings={this.props.settings?.widgetSettings}
            languageCode={this.state.language}
            closeButton={this.getCloseButton()}
            onBackButtonClick={() => this.handleBackButtonClick()}
            onSearchButtonClick={() => this.handleSearchButtonClick()}
            showSearch={false}
          />
        )}
        <FormGroupSelectionMenu
          formsOnly={this.props.formOnly || this.props.formsOnly}
          showFooter={true}
          header={<></>}
          languageCode={this.state.language}
          widgetSettings={this.props.settings?.widgetSettings}
          formGroups={formGroups}
          onSelect={(formGroup) => this.handleFormGroupClick(formGroup)}
        />
      </>
    );
  }

  getFormSelectionContent(): React.ReactNode {
    const selectedFormGroup = this.state.selectedFormGroup;
    const closeButton = this.props.formsOnly ? null : this.getCloseButton();

    return (
      <>
        <Header
          widgetSettings={this.props.settings?.widgetSettings}
          languageCode={this.state.language}
          closeButton={closeButton}
          onBackButtonClick={() => this.handleBackButtonClick()}
          onSearchButtonClick={() => this.handleSearchButtonClick()}
          showSearch={false}
        />
        <FormSelectionMenu
          formsOnly={this.props.formOnly || this.props.formOnly}
          showFooter={true}
          header={<></>}
          languageCode={this.state.language}
          widgetSettings={this.props.settings?.widgetSettings}
          contextSettings={this.props.settings?.widgetSettings}
          formGroup={selectedFormGroup}
          onSelect={(form) => this.handleFormClick(form)}
        />
      </>
    );
  }

  getFormContent(): React.ReactNode {
    const closeButton = this.props.formsOnly || this.props.formOnly ? null : this.getCloseButton();
    const showSearch = this.props.formsOnly || this.props.formOnly ? false : true;

    return (
      <Form
        formsOnly={this.props.formsOnly || this.props.formOnly}
        selectedForm={this.state.selectedForm}
        formContent={this.state.formContent}
        formState={this.state.formState}
        closeButton={closeButton}
        showSearch={showSearch}
        hideHeader={this.props.formOnly}
        setFormState={this.setFormState}
        setValidatedForm={(validatedForm) => this.setState({validatedForm})}
        showSuggestedQuestions={(questions) => {
          this.navigateTo({
            suggestedQuestions: questions,
            content: WidgetContent.QA_SUGGESTIONS,
          });
        }}
        language={this.state.language}
        navigateTo={(newState) => this.navigateTo(newState)}
        handleBackButtonClick={() => this.handleBackButtonClick()}
        handleSearchButtonClick={() => this.handleSearchButtonClick()}
      />
    );
  }

  getPhoneContacts(): React.ReactNode {
    const text = this.props.settings?.settings?.langs?.[this.state.language]?.phoneContactsText;
    const phoneContacts: PhoneContact[] =
      this.props.settings?.settings?.langs?.[this.state.language]?.phoneContacts || [];

    return (
      <React.Fragment>
        <header className="faq-widget-header">
          <HeaderBackBtn
            handleBackButtonClick={() => this.handleBackButtonClick()}
            text={this.props.settings?.widgetSettings?.langs?.[this.state.language]?.backButtonText}
          />
          <div className="faq-widget-header-right">
            <SearchBtn
              handleSearchButtonClick={() => this.handleSearchButtonClick()}
              buttonColor={this.props.settings?.widgetSettings?.categoryButtonColor}
              text={this.props.settings?.widgetSettings?.langs?.[this.state.language]?.searchButtonText}
            />
            {this.getCloseButton()}
          </div>
        </header>
        <main className="faq-widget-main">
          <div className="faq-widget-main-top">
            <h2>{text}</h2>
            <div className="flex flex-col gap-2">
              {phoneContacts?.map((contact, i) => {
                const flagUrl = getCountryFlag(contact.countryCode);

                return (
                  <div key={i}>
                    <p>{contact.description}</p>
                    <span className="flex gap-1 items-center">
                      {flagUrl && <img src={flagUrl} alt={contact.countryCode} className="w-4 h-4" />}
                      <a href={`tel:${contact.phoneNumber}`} className="text-blue-500">
                        {contact.phoneNumber}
                      </a>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          {this.props.settings?.widgetSettings?.footerEnabled && <Footer />}
        </main>
      </React.Fragment>
    );
  }

  getAssistantChatContent(): React.ReactNode {
    const isAssistantChatOnlyMode = this.props.settings?.widgetSettings?.assistantChatOnlyMode;

    return (
      <>
        {this.props.settings?.widgetSettings?.assistantChannel === AssistantChannel.CHAT && (
          <Header
            widgetSettings={this.props.settings?.widgetSettings}
            languageCode={this.state.language}
            showTitle={isAssistantChatOnlyMode ? true : false}
            showSearch={isAssistantChatOnlyMode ? false : true}
            showBackButton={isAssistantChatOnlyMode ? false : true}
            closeButton={this.getCloseButton()}
            onBackButtonClick={() => this.handleBackButtonClick()}
            onSearchButtonClick={() => this.handleSearchButtonClick()}
          />
        )}
        <div
          className={`${
            this.props.settings?.widgetSettings?.assistantChannel === AssistantChannel.CHAT
              ? "mt-[62px] h-[calc(100dvh-62px)]"
              : "h-full"
          }`}
        >
          <AssistantChat
            currentKnowledgeBase={this.props.currentKnowledgeBase}
            widgetSettings={this.props.settings?.widgetSettings}
            assistantSettings={this.props.settings?.assistantSettings}
            requireEmail={this.props.settings?.widgetSettings?.requireEmailForAssistantChat}
            showFeedbackSection={true}
            channel={this.props.settings?.widgetSettings?.assistantChannel || AssistantChannel.CHAT}
            language={this.state.language}
            navigateTo={(content: WidgetContent) => this.navigateTo({content})}
          />
        </div>
      </>
    );
  }

  getCloseButton() {
    if (this.state.isMobileView === false) return null;

    return (
      <button
        className="faq-widget-close-button transform rotate-180"
        aria-label={this.props.t("CLOSE")}
        title={this.props.t("CLOSE")}
        onClick={() => this.handleCloseButtonClick()}
        style={{
          backgroundColor: this.props.settings?.widgetSettings?.buttonColor,
        }}
      >
        <Icon name="chevron-up" iconSize="32px" color="#fff" />
      </button>
    );
  }

  handleCloseButtonClick = () => {
    window.parent.postMessage("toggleWidget", "*");
  };

  async upVoteQuestion() {
    try {
      await UpvoteQuestion(
        this.props.currentKnowledgeBase.id,
        this.state.selectedQuestion.categoryId,
        this.state.selectedQuestion.id,
        this.state.selectedQuestion.langs?.[this.state.language]?.id,
      );
    } catch (error) {
      console.error("Error upvoting question:", error);
    } finally {
      this.setState({isFeedbackGiven: true});
    }
  }

  async downVoteQuestion() {
    try {
      await DownvoteQuestion(
        this.props.currentKnowledgeBase.id,
        this.state.selectedQuestion.categoryId,
        this.state.selectedQuestion.id,
        this.state.selectedQuestion.langs?.[this.state.language]?.id,
      );

      const {widgetSettings, websiteSettings} = this.props.settings || {};
      const {redirectToFormId, formEnabled} = widgetSettings || {};

      if (redirectToFormId !== 0 && formEnabled) {
        const form = this.findRedirectForm(redirectToFormId);
        if (form) {
          this.handleFormClick(form);
          return;
        }
      }

      websiteSettings?.mailbox
        ? this.navigateTo({content: WidgetContent.MAIL})
        : this.setState({isFeedbackGiven: true});
    } catch (error) {
      console.error("Error downvoting question:", error);
    }
  }

  findRedirectForm(redirectToFormId) {
    const formGroups = this.props.settings.widgetSettings.langs?.[this.state.language]?.formGroups;
    return formGroups?.flatMap((fg) => fg.forms).find((f) => f.id === redirectToFormId);
  }

  handlePhoneContactsIconClick() {
    this.navigateTo({
      content: WidgetContent.PHONE_CONTACTS,
    });
  }

  async handleFormGroupClick(formGroup) {
    this.navigateTo({
      content: WidgetContent.FORM_SELECT,
      selectedFormGroup: formGroup,
    });
  }

  async handleFormClick(selectedForm: IForm) {
    try {
      const formContent = await this.getWidgetForm(selectedForm.id);
      this.setInitialFormState(formContent);
      this.setState({formContent}, () => {
        this.navigateTo({
          content: WidgetContent.FORM,
          selectedForm,
        });
      });
    } catch (error) {
      console.error("error: ", error);
      AlertError(this.props.t("FORM_NOT_FOUND"));
    }
  }

  setInitialFormState(formContent: FormContent) {
    const formState = {};
    formContent.layout.elements.forEach((element) => {
      switch (element.ui_type) {
        case "input":
        case "text":
        case "textarea":
          formState[element.ref] = "";
          break;
        case "list":
          formState[element.ref] = [""];
          break;
        case "checkbox":
          formState[element.ref] = false;
          break;
        case "select":
        case "radio":
          formState[element.ref] = null;
          break;
        default:
          break;
      }
    });
    this.setState({formState});
  }

  getFormData(): FormData {
    const formData = new FormData();

    Object.keys(this.state.validatedForm).forEach((key) => {
      const value = this.state.validatedForm[key];
      if (value instanceof File) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((v) => {
          formData.append(`${key}[]`, v);
        });
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  }

  async submitForm(formId: number) {
    const formData = this.getFormData();

    this.setState({isSubmitting: true});
    try {
      await PostWidgetForm(formId, formData);
      await this.PostWidgetFormSubmission(formId);
      this.setState({content: WidgetContent.MAIN, formState: {}});
      AlertSuccess(this.props.t("FORM_SUBMIT_SUCCESS"));
    } catch (error) {
      console.error("Form submission error:", error);
      AlertError(this.props.t("FORM_SUBMIT_FAILED"));
    } finally {
      this.setState({isSubmitting: false});
    }
  }

  async PostWidgetFormSubmission(formId: number): Promise<void> {
    const formTitle = this.state.formContent.layout.title;
    const processedForm = processFormForAssistant(this.state.validatedForm, FORM_FIELDS_TO_EXCLUDE);
    const formData = {...processedForm, formTitle};

    await PostWidgetFormSubmission(this.props.currentKnowledgeBase.id, formId, formData);
  }

  handleLinkClick = (event) => {
    const key = decodeURIComponent(event.target.getAttribute("data-key"));
    const type = event.target.className.includes("question")
      ? "question"
      : event.target.className.includes("form")
      ? "form"
      : "category";

    if (type === "question") {
      const question = this.props.questions.find((q) => q.id === parseInt(key));
      if (!question) {
        AlertError(this.props.t("QUESTION_NOT_FOUND"));
        return;
      }

      this.handleQuestionClick(question);
    } else if (type === "category") {
      const category = this.props.categories.find((c) => c.id === parseInt(key));
      if (!category) {
        AlertError(this.props.t("CATEGORY_NOT_FOUND"));
        return;
      }
      this.handleCategoryClick(parseInt(key));
    } else if (type === "form") {
      const form = this.props.settings?.widgetSettings?.langs?.[this.state.language]?.formGroups
        .flatMap((fg) => fg.forms)
        .find((f) => f.id === parseInt(key));
      if (!form) {
        AlertError(this.props.t("FORM_NOT_FOUND"));
        return;
      }

      this.handleFormClick(form);
    }
  };

  attachLinkHandlers() {
    document
      .querySelectorAll(".internal-link-question, .internal-link-form, .internal-link-category")
      .forEach((element) => {
        element.addEventListener("click", this.handleLinkClick);
      });
  }

  removeLinkHandlers() {
    document
      .querySelectorAll(".internal-link-question, .internal-link-form, .internal-link-category")
      .forEach((element) => {
        element.removeEventListener("click", this.handleLinkClick);
      });
  }

  handleFAQWidgetClick() {
    this.setState({showFAQPopup: true});
  }

  async handleChatIconClick(e) {
    this.navigateTo({content: WidgetContent.CHAT});
  }

  async handleFormIconClick() {
    const formGroups = this.props.settings?.widgetSettings?.langs?.[this.state.language]?.formGroups;
    const singleFormGroup = formGroups?.[0];

    if (formGroups?.length === 1 && singleFormGroup?.forms?.length === 1) {
      this.handleFormClick(singleFormGroup.forms[0]);
    } else if (formGroups?.length > 1) {
      this.navigateTo({content: WidgetContent.FORM_GROUP_SELECT});
    } else {
      this.setState({selectedFormGroup: singleFormGroup}, () => {
        this.navigateTo({content: WidgetContent.FORM_SELECT});
      });
    }
  }

  async handleMailIconClick() {
    this.navigateTo({
      content: WidgetContent.MAIL,
    });
  }

  async handleMailSubmitClick() {
    if (!this.validateMailInput(this.state.mailAddress)) return;

    const {questions} = scanKnowledgeBase(
      this.state.mailBody,
      this.props.questions,
      this.props.categories,
      this.state.language,
    );
    if (questions?.length > 0) {
      this.setState({suggestedQuestions: questions}, () => {
        this.navigateTo({
          content: WidgetContent.QA_SUGGESTIONS,
        });
      });
    } else {
      this.sendMail();
    }
  }

  async handleCategoryClick(categoryId: number) {
    this.navigateTo({
      content: WidgetContent.CATEGORY,
      selectedCategoryId: categoryId,
    });
  }

  async handleQuestionClick(question: Question) {
    this.navigateTo({
      content: WidgetContent.QUESTION,
      selectedQuestion: question,
      selectedCategoryId: question.categoryId,
    });
    UpdateQuestionViews(
      this.props.currentKnowledgeBase.id,
      question.categoryId,
      question.id,
      question.langs?.[this.state.language]?.id || 0,
    );
  }

  async handleSearchButtonClick() {
    this.navigateTo({
      content: WidgetContent.MAIN,
    });
  }

  navigateTo(newState) {
    if (newState.content === WidgetContent.MAIN) {
      this.setState({
        ...newState,
        navigationHistory: [],
      });
    } else {
      this.setState((prevState) => ({
        navigationHistory: [
          ...prevState.navigationHistory,
          {
            content: this.state.content,
            selectedCategoryId: this.state.selectedCategoryId,
            selectedQuestion: this.state.selectedQuestion,
          },
        ],
        ...newState,
      }));
    }
  }

  handleBackButtonClick() {
    const lastState = this.state.navigationHistory[this.state.navigationHistory.length - 1];

    if (lastState === undefined) {
      this.navigateTo({
        content: WidgetContent.MAIN,
      });
    } else {
      this.setState((prevState) => ({
        ...lastState,
        navigationHistory: prevState.navigationHistory.slice(0, -1),
      }));
    }
  }

  async sendMail() {
    const mail: Mail = {
      to: this.props.settings?.websiteSettings?.mailbox?.split(","),
      from: "no-reply@connectel.io",
      subject: this.state.mailSubject,
      html: `
      From: ${this.state.mailAddress} <br/><br/>
      Message: ${this.state.mailBody}`,
      replyTo: [this.state.mailAddress],
    };

    this.setState({isSubmitting: true});
    try {
      await PostMail(mail);
      AlertSuccess(this.props.t("MAIL_SUCCESS"));
      this.setState({content: WidgetContent.MAIN, mailAddress: "", mailBody: ""});
    } catch (error) {
      console.error("error: ", error);
      AlertError(this.props.t("MAIL_FAILED"));
    } finally {
      this.setState({isSubmitting: false});
    }
    const enquiry = {
      text: this.state.mailBody,
    };
    await CreateEnquiry(enquiry);
  }

  setFormState = (formState) => {
    this.setState({formState});
  };

  async getWidgetForm(formId: number): Promise<FormContent> {
    const response = await GetWidgetForm(formId);
    return response.data;
  }

  validateMailInput = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    let isValidEmail = emailRegex.test(email);

    this.setState({
      fieldErrors: {
        mailAddress: isValidEmail ? "" : this.props.t("INVALID_EMAIL"),
      },
    });

    return isValidEmail;
  };

  initializeLanguage() {
    const userLanguage = navigator.language;
    const fallbackLanguage = this.props.settings?.settings.fallbackLanguage.code;
    const urlLanguage = this.props.params.lang;
    const availableLanguages = this.props.settings?.settings.supportedLanguages.map((lang) => lang.code) || [];
    let language;

    if (availableLanguages.includes(urlLanguage)) {
      language = urlLanguage;
    } else if (availableLanguages.includes(userLanguage)) {
      language = userLanguage;
    } else if (availableLanguages.includes(userLanguage.split("-")[0])) {
      language = userLanguage.split("-")[0];
    } else {
      language = fallbackLanguage;
    }

    this.setState({
      language,
    });
  }

  handleParentMessage = (event: MessageEvent) => {
    if (event.data.type === "WINDOW_WIDTH") {
      this.setState({isMobileView: event.data.width < 640});
    }

    if (event.data === "WIDGET_OPENED" && this.props.questions.length === 0) {
      this.getQuestions();
    }
  };

  async getQuestions() {
    const questions = await GetQuestions(this.props.currentKnowledgeBase.id);
    SetAllQuestions(questions);
  }
}

const mapStateToProps = (state: {
  knowledgeBase: {knowledgeBases: KnowledgeBase[]; currentKnowledgeBase: KnowledgeBase};
  categories: {categories: Category[]};
  questions: Question[];
  settings: {settings: KBSettings};
  language: {language: string};
}) => {
  return {
    categories: state.categories?.categories || [],
    questions: state.questions || [],
    knowledgeBases: state.knowledgeBase?.knowledgeBases,
    currentKnowledgeBase: state.knowledgeBase?.currentKnowledgeBase,
    settings: state.settings?.settings,
    language: state.language?.language,
  };
};

export default connect(mapStateToProps)(withTranslation()(withParams(withHtmlParser(Widget))));
